/**
 * Signup-access storage: the referral code and promo campaign slug a visitor
 * carries into signup, persisted in localStorage with expiry. Storage can be
 * denied or full (privacy modes, quota), so every operation degrades to
 * "no stored access" instead of throwing.
 */
import { stripLocalePrefix } from '@/i18n/surfaces'
import { getLocalStorage } from '@/lib/safeLocalStorage'

// Referral code storage keys
const REFERRAL_CODE_KEY = 'referralCode'
const REFERRAL_CODE_EXPIRY_KEY = 'referralCodeExpiry'
// Capture context: a shared project link vs a marketing page (last-click, stored with the code)
const REFERRAL_SOURCE_KEY = 'referralSource'
const REFERRAL_LANDING_PATH_KEY = 'referralLandingPath'
const REFERRAL_EXPIRY_DAYS = 30

// Promo campaign storage keys
const PROMO_SLUG_KEY = 'promoSlug'
const PROMO_SLUG_EXPIRY_KEY = 'promoSlugExpiry'
const PROMO_EXPIRY_DAYS = 30

// Checkout promo code carried in by `/pricing?code=X` (workshop thank-you
// emails). Not signup access: it only pre-fills the coupon field on the plan
// cards once the visitor is signed in, so a logged-out click survives signup.
const PROMO_CODE_KEY = 'promoCode'
const PROMO_CODE_EXPIRY_KEY = 'promoCodeExpiry'
const PROMO_CODE_EXPIRY_DAYS = 30

export type ReferralSource = 'project_link' | 'marketing' | 'motion_share' | 'martini_bar' | AiAgentReferralSource

/** The sentinel ref code carried by the camera-motion share CTA (/m/<id>). Not a
 * real referral code — no creator is rewarded; it exists purely so signups from a
 * share attribute to `motion_share` instead of being mislabeled `marketing`. */
export const MOTION_SHARE_REF_CODE = 'motion_share'

/** Sentinel stored by opening /bar: the Martini Bar launch page is open to anyone, so landing
 * there grants signup access like a /m/ share does. Attribution only, never redeemed. */
export const MARTINI_BAR_REF_CODE = 'martini_bar'

/** Sentinel ref codes published in llms.txt and /docs/mcp for AI assistants that
 * recommend Martini to their users (?ref=claude etc.). Like motion_share: nobody
 * is rewarded — the code grants signup access and attributes the signup to the
 * referring assistant via `referralSource`. */
export const AI_AGENT_REF_CODE_SOURCES = {
  'ai-agent': 'ai_agent',
  claude: 'ai_claude',
  chatgpt: 'ai_chatgpt',
  perplexity: 'ai_perplexity',
} as const
type AiAgentReferralSource = (typeof AI_AGENT_REF_CODE_SOURCES)[keyof typeof AI_AGENT_REF_CODE_SOURCES]

const REFERRAL_SOURCES: readonly string[] = [
  'project_link',
  'marketing',
  'motion_share',
  'martini_bar',
  ...Object.values(AI_AGENT_REF_CODE_SOURCES),
]

function aiAgentSource(code: string): AiAgentReferralSource | undefined {
  // Lowercase first — assistants paraphrase published links (?ref=Claude).
  // Object.hasOwn, not `in`: a ?ref=toString must not match a prototype property
  const normalized = code.toLowerCase()
  return Object.hasOwn(AI_AGENT_REF_CODE_SOURCES, normalized)
    ? AI_AGENT_REF_CODE_SOURCES[normalized as keyof typeof AI_AGENT_REF_CODE_SOURCES]
    : undefined
}

/** Whether a ref code is an attribution-only sentinel: never redeemed for olives,
 * never looked up as a referrer, never allowed to overwrite a genuine referrer's
 * stored code. Case-insensitive; genuine Autumn codes are matched exactly elsewhere. */
export function isSentinelRefCode(code: string): boolean {
  const normalized = code.toLowerCase()
  return (
    normalized === MOTION_SHARE_REF_CODE || normalized === MARTINI_BAR_REF_CODE || aiAgentSource(code) !== undefined
  )
}

/**
 * Fired on `window` after a referral code or promo slug is stored in this tab.
 * Marketing CTAs (via useStoredSignupAccess) and the promo-campaign hook listen
 * for it to refresh their signup state; referral and promo writes share it
 * since both grant access.
 */
export const SIGNUP_ACCESS_STORED_EVENT = 'martini:signup-access-stored'

function deriveReferralSource(code: string, landingPath: string): ReferralSource {
  // Same case-insensitivity as isSentinelRefCode — a classified sentinel must
  // never fall through to path-derived attribution.
  if (code.toLowerCase() === MOTION_SHARE_REF_CODE) return 'motion_share'
  if (code.toLowerCase() === MARTINI_BAR_REF_CODE) return 'martini_bar'
  const aiSource = aiAgentSource(code)
  if (aiSource) return aiSource
  const stripped = stripLocalePrefix(landingPath).pathname
  if (stripped.startsWith('/p/')) return 'project_link'
  if (stripped.startsWith('/m/')) return 'motion_share'
  return 'marketing'
}

interface ExpiringRecord {
  read(): string | null
  write(value: string, context?: ReadonlyArray<readonly [string, string]>): boolean
  clear(): void
  ownsKey(key: string): boolean
  unsuppress(): void
}

/**
 * A localStorage value paired with an expiry key, written so a partial
 * multi-key write can never read back as a valid record: the expiry is
 * invalidated first and restored last. When a failed write or clear may have
 * left stale keys behind (storage can deny removals too), the record
 * suppresses reads until another tab replaces it.
 */
function createExpiringRecord(config: {
  valueKey: string
  expiryKey: string
  ttlDays: number
  /** Extra keys written alongside the value and cleared with it. */
  contextKeys?: readonly string[]
}): ExpiringRecord {
  const { valueKey, expiryKey, ttlDays, contextKeys = [] } = config
  const allKeys = [valueKey, expiryKey, ...contextKeys]
  let suppressed = false

  function removeAll(): boolean {
    const storage = getLocalStorage()
    if (!storage) return false
    try {
      for (const key of allKeys) storage.removeItem(key)
      return true
    } catch {
      return false
    }
  }

  function clear(): void {
    suppressed = !removeAll()
  }

  return {
    read(): string | null {
      if (suppressed) return null
      const storage = getLocalStorage()
      if (!storage) return null
      const value = storage.getItem(valueKey)
      const expiryStr = storage.getItem(expiryKey)
      if (!value || !expiryStr) return null
      if (Date.now() > parseInt(expiryStr, 10)) {
        clear()
        return null
      }
      return value
    },
    write(value: string, context: ReadonlyArray<readonly [string, string]> = []): boolean {
      let stored = false
      const storage = getLocalStorage()
      if (storage) {
        try {
          storage.removeItem(expiryKey)
          for (const [key, contextValue] of context) storage.setItem(key, contextValue)
          storage.setItem(valueKey, value)
          storage.setItem(expiryKey, String(Date.now() + ttlDays * 24 * 60 * 60 * 1000))
          stored = true
        } catch {
          // Quota or denial mid-write: roll back so no partial record remains. If
          // removals are denied too, the expiry removal above already happened (or
          // failed first), and suppression below keeps any leftover record unread.
          removeAll()
        }
      }
      suppressed = !stored
      return stored
    },
    clear,
    ownsKey: (key: string) => key === valueKey || key === expiryKey,
    unsuppress: () => {
      suppressed = false
    },
  }
}

const referralRecord = createExpiringRecord({
  valueKey: REFERRAL_CODE_KEY,
  expiryKey: REFERRAL_CODE_EXPIRY_KEY,
  ttlDays: REFERRAL_EXPIRY_DAYS,
  contextKeys: [REFERRAL_SOURCE_KEY, REFERRAL_LANDING_PATH_KEY],
})

const promoRecord = createExpiringRecord({
  valueKey: PROMO_SLUG_KEY,
  expiryKey: PROMO_SLUG_EXPIRY_KEY,
  ttlDays: PROMO_EXPIRY_DAYS,
})

const promoCodeRecord = createExpiringRecord({
  valueKey: PROMO_CODE_KEY,
  expiryKey: PROMO_CODE_EXPIRY_KEY,
  ttlDays: PROMO_CODE_EXPIRY_DAYS,
})

const records = [referralRecord, promoRecord]

// Re-enable suppressed records when another tab changes their access-bearing
// keys. Cross-tab writers invalidate expiry first and restore it last, so each
// event can safely re-check the record without exposing a partial replacement;
// `key === null` is a cross-tab storage.clear(). Non-browser environments can
// carry a partial `window` stub, so require the listener API rather than the global.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('storage', event => {
    for (const record of records) {
      if (event.key === null || record.ownsKey(event.key)) record.unsuppress()
    }
  })
}

/**
 * Whether a cross-tab `storage` event touched a signup-access record, i.e.
 * whether listeners need to re-read signup-access state.
 */
export function shouldSyncSignupAccessFromStorageEvent(key: string | null): boolean {
  return key === null || records.some(record => record.ownsKey(key))
}

/**
 * Capture referral code from URL if present and store in localStorage.
 * Uses last-click attribution (newer codes overwrite older ones).
 */
export function captureReferralCode(): void {
  if (typeof window === 'undefined') return

  const params = new URLSearchParams(window.location.search)
  const refCode = params.get('ref')

  if (refCode) {
    storeReferralCode(refCode)
    console.log(`[Referral] Captured referral code: ${refCode}`)
  }
}

/**
 * Store a referral code in localStorage with expiry, along with where it was
 * captured (shared project link vs marketing page) for signup attribution.
 */
export function storeReferralCode(code: string): void {
  if (typeof window === 'undefined') return
  // Sentinels are SOURCE signals, not real referral codes. Never let one
  // overwrite a genuine referrer's code — last-click would silently steal that
  // referrer's attribution and olives when a referred user later opens a
  // sentinel-carrying link (a /m/<id> share, an assistant's ?ref=claude link).
  if (isSentinelRefCode(code)) {
    const existing = getStoredReferralCode()
    if (existing && !isSentinelRefCode(existing)) return
  }
  const landingPath = window.location.pathname.slice(0, 200)
  const source: ReferralSource = deriveReferralSource(code, landingPath)
  const stored = referralRecord.write(code, [
    [REFERRAL_SOURCE_KEY, source],
    [REFERRAL_LANDING_PATH_KEY, landingPath],
  ])
  if (!stored) return
  window.dispatchEvent(new CustomEvent(SIGNUP_ACCESS_STORED_EVENT, { detail: { code } }))
}

/**
 * Get the stored referral capture context (where the ?ref= link landed).
 * Returns null when no unexpired referral code is stored.
 */
export function getStoredReferralContext(): { source: ReferralSource; landingPath: string | null } | null {
  if (!getStoredReferralCode()) return null
  const storage = getLocalStorage()
  if (!storage) return null
  const source = storage.getItem(REFERRAL_SOURCE_KEY)
  const landingPath = storage.getItem(REFERRAL_LANDING_PATH_KEY)
  // When the source key is present, trust it. When it's absent (a ?ref captured
  // before this field shipped), don't blindly default to 'marketing' — infer
  // from the landing path if we have one, so a pre-existing project-link capture
  // isn't mislabeled. Falls back to 'marketing' only when nothing is known.
  const resolvedSource: ReferralSource =
    source && REFERRAL_SOURCES.includes(source)
      ? (source as ReferralSource)
      : landingPath && stripLocalePrefix(landingPath).pathname.startsWith('/p/')
        ? 'project_link'
        : landingPath && stripLocalePrefix(landingPath).pathname.startsWith('/m/')
          ? 'motion_share'
          : 'marketing'
  return { source: resolvedSource, landingPath }
}

/**
 * Get the stored referral code if it exists and hasn't expired.
 */
export function getStoredReferralCode(): string | null {
  return referralRecord.read()
}

/**
 * Clear the stored referral code and its capture context (after successful redemption).
 */
export function clearStoredReferralCode(): void {
  referralRecord.clear()
}

/**
 * Store a promo campaign slug in localStorage with expiry.
 */
export function storePromoSlug(slug: string): void {
  if (typeof window === 'undefined') return
  if (!promoRecord.write(slug)) return
  window.dispatchEvent(new CustomEvent(SIGNUP_ACCESS_STORED_EVENT, { detail: { promoSlug: slug } }))
}

/**
 * Get the stored promo slug if it exists and hasn't expired.
 */
export function getStoredPromoSlug(): string | null {
  return promoRecord.read()
}

/**
 * Clear the stored promo slug (after successful redemption).
 */
export function clearStoredPromoSlug(): void {
  promoRecord.clear()
}

/** Remember a checkout promo code from a `?code=` link until a signed-in plan page has resolved it. */
export function storePromoCode(code: string): void {
  if (typeof window === 'undefined') return
  const trimmed = code.trim()
  if (!trimmed) return
  promoCodeRecord.write(trimmed)
}

export function getStoredPromoCode(): string | null {
  return promoCodeRecord.read()
}

/** Clear the stored checkout promo code (consumed as soon as a plan page resolves it). */
export function clearStoredPromoCode(): void {
  promoCodeRecord.clear()
}

/**
 * Whether the visitor holds any stored signup-access code (referral or promo).
 * Single source of truth for the marketing CTAs and the SignUpModal gate —
 * both getters self-clean expired entries.
 */
/**
 * Open-signups switch (build-time): when set, every visitor counts as holding
 * signup access — the waitlist gate never renders anywhere (nav click
 * handlers, the signup modal, and the reactive hook all route through here).
 * Dark by default; the single launch lever for retiring the waitlist.
 */
export const SIGNUPS_OPEN = process.env.NEXT_PUBLIC_SIGNUPS_OPEN === '1'

export function hasStoredSignupAccess(): boolean {
  return SIGNUPS_OPEN || !!getStoredReferralCode() || !!getStoredPromoSlug()
}
